import React, { useState, useEffect } from 'react';
import {
    Typography,
    Button,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Box,
    CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom'
import SnackbarEx from '../Helpers/SnackbarEx.jsx';
import PostData from '../../services/PostData.js';
import useApiData from '../../services/useApiData.js'
import { CreditCard, Receipt, Upgrade, OpenInNew } from '@mui/icons-material';

export default function BillingTab() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [subscription, setSubscription] = useState(null)
    const [invoices, setInvoices] = useState([])
    const location = useLocation();
    const [openTestSnack, setOpenTestSnack] = useState({
        color: "",
        message: "",
        open: false
    })

    const { data: billingInfo,
        error: billingError,
        isLoading: loadBilling,
        refetch: refetchBilling } = useApiData("api/broylesllc/billing/user-information");

    useEffect(() => {
        setSubscription(billingInfo?.subscription || null)
        setInvoices(billingInfo?.invoices || [])
    }, [billingInfo])

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (params.get('success')) {
            setOpenTestSnack({
                color: "success",
                message: "Subscription updated successfully! It may take a minute to reflect.",
                open: true
            })
            refetchBilling()
            navigate(window.location.pathname, { replace: true });
        }
        if (params.get('canceled')) {
            setOpenTestSnack({
                color: "info",
                message: "Upgrade canceled. No changes were made.",
                open: true
            })
        }
    }, [location]);

    // Example handler for Stripe Customer Portal
    const handleManageBilling = async () => {
        setLoading(true);
        try {
            // Calling the endpoint we just created above
            const result = await PostData({}, 'broylesllc/billing/create-portal', null);

            // PostData usually returns the raw response in 'data' based on your previous snippet
            if (result.success && result.data?.url) {
                window.location.href = result.data.url;
            } else {
                setOpenTestSnack({
                    color: "error",
                    message: "Could not open billing portal. Please try again.",
                    open: true
                });
            }
        } catch (err) {
            setOpenTestSnack({
                color: "error",
                message: "Could not open billing portal. Please try again.",
                open: true
            });
        } finally {
            setLoading(false);
        }
    };

    const handleUpgrade = async () => {
        try {
            setLoading(true)
            // We call your Express backend, which will use the Stripe Secret Key 
            const { success, error, load, data } = await PostData({ priceId: 'price_1SvTXPCsgkj3dcrS2YlXKjpz' }, 'broylesllc/billing/upgrade-plan', null);
            // const { success, error, load, data } = await PostData({ priceId: 'price_1Sv31CERFzdK7XsnHKDeVnMY' }, 'broylesllc/billing/upgrade-plan', null);
            if (success && data.url) {
                setLoading(false)
                // Redirect the user to Stripe's secure checkout page
                window.location.href = data?.url;
            } else {
                setLoading(false)
                setOpenTestSnack({
                    color: "error",
                    message: "Could not open billing portal. Please try again.",
                    open: true
                });
            }
        } catch (err) {
            setLoading(false)
            setOpenTestSnack({
                color: "error",
                message: "Could not open billing portal. Please try again.",
                open: true
            });
        }
    };

    return (
        <>
            <div style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                // paddingTop: "20px",
                padding: "10px 10px 0 10px",
                boxSizing: 'border-box'
            }}>
                <div className="card-header bg-white" style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="h5" component="h1">
                        Billing & Subscription
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '13px !important', paddingTop: '2px' }}>
                        Manage your subscription plan, payment methods, and download invoices.
                    </Typography>
                </div>

                {loadBilling ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <div className="card-body px-0" style={{ overflowY: 'auto' }}>
                        {/* Section 1: Current Plan */}
                        <Box sx={{
                            py: 3, px: 2, display: 'flex',
                            justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <Box>
                                <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Current Plan</Typography>
                                <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                                    {subscription
                                        ? `${subscription.description} | $${(subscription?.items?.data?.[0]?.price?.unit_amount / 100).toFixed(2)}/mo`
                                        : "Awaiting subscription"}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {subscription
                                        ? `Next renewal date is: ${new Date(subscription?.items?.data?.[0]?.current_period_end * 1000).toLocaleDateString()}`
                                        : 'Upgrade to continue using service.'}
                                </Typography>
                            </Box>
                            <Button
                                variant={"outlined"}
                                loading={loading}
                                onClick={handleUpgrade}
                                startIcon={<Upgrade />}
                                sx={{ borderRadius: '8px', textTransform: 'none' }}
                            >
                                {subscription ? 'Change Plan' : 'Upgrade Plan'}
                            </Button>
                        </Box>

                        <Divider />

                        {/* Section 2: Payment Method */}
                        {subscription && (
                            <>
                                <Box sx={{ py: 3, px: 2 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '14px', mb: 2 }}>Payment Method</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', p: 2, border: '1px solid #eaecf0', borderRadius: '8px', justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <CreditCard color="action" />
                                            <Box>
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                    Payment Method on File
                                                </Typography>
                                                <Typography sx={{ fontSize: '12px', color: 'gray' }}>
                                                    Managed securely via Stripe
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Button
                                            size="small"
                                            onClick={handleManageBilling}
                                            endIcon={<OpenInNew sx={{ fontSize: '14px !important' }} />}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Update
                                        </Button>
                                    </Box>
                                </Box>
                                <Divider />
                            </>
                        )}

                        {/* Section 3: Invoices */}
                        <Box sx={{ py: 3, px: 2 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px', mb: 2 }}>Recent Invoices</Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead sx={{ backgroundColor: '#f9fafb' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontSize: '12px', fontWeight: 600 }}>Date</TableCell>
                                            <TableCell sx={{ fontSize: '12px', fontWeight: 600 }}>Amount</TableCell>
                                            <TableCell sx={{ fontSize: '12px', fontWeight: 600 }}>Status</TableCell>
                                            <TableCell align="right"></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invoices.length > 0 ? invoices.map((invoice) => (
                                            <TableRow key={invoice.id}>
                                                <TableCell sx={{ fontSize: '13px' }}>
                                                    {new Date(invoice.created * 1000).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '13px' }}>
                                                    ${(invoice.total / 100).toFixed(2)}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={invoice.status}
                                                        size="small"
                                                        color={invoice.status === 'paid' ? 'success' : 'default'}
                                                        variant="outlined"
                                                        sx={{ height: '20px', fontSize: '11px', textTransform: 'capitalize' }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        component="a"
                                                        href={invoice.invoice_pdf}
                                                        target="_blank"
                                                        startIcon={<Receipt sx={{ fontSize: '16px !important' }} />}
                                                    >
                                                        PDF
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        )) : (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                                    No invoices found.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </div> // This closes card-body

                )}

                {openTestSnack.open && (
                    <SnackbarEx
                        color={openTestSnack.color}
                        message={openTestSnack.message}
                        open={openTestSnack.open}
                        close={() => setOpenTestSnack({ color: "", message: "", open: false })}
                    />
                )}
            </div>
        </> // This closes the main card div
    );
};